import { useCallback } from 'react'
import i18n from '@/i18n'
import { projectApi } from '@/api/projectApi'
import { exportDiagramAsPng, exportDiagramAsSvg, sanitizeFilename } from '@/features/export-image/utils/exportDiagramImage'
import { useDiagramStore, useSelectionStore, useSettingsStore } from '@/stores'
import { useDraftStore } from '@/stores/draftStore'
import { useTabViewStore } from '@/stores/tabViewStore'

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

/** Resolve which server-side model the export should read: the live draft, or the commit being viewed. */
function resolveExportSource(): { projectId: number; source: 'draft' | 'commit'; commitId: number | null } | null {
  const { currentProjectId, mode, viewingCommitId } = useDraftStore.getState()
  if (!currentProjectId) return null
  const viewingCommit = mode === 'view' && viewingCommitId != null
  return {
    projectId: currentProjectId,
    source: viewingCommit ? 'commit' : 'draft',
    commitId: viewingCommit ? viewingCommitId : null,
  }
}

export function useExportActions() {
  const projectName = useDiagramStore((state) => state.projectName)
  const tables = useDiagramStore((state) => state.tables)
  const rels = useDiagramStore((state) => state.rels)
  const resolvedTheme = useSettingsStore((state) => state.resolvedTheme)
  const showGrid = useSettingsStore((state) => state.showGrid)
  const language = useSettingsStore((state) => state.language)
  const hiddenTableIds = useTabViewStore((state) => state.hiddenTableIds)

  const resolveBaseName = useCallback(
    (override?: string) => sanitizeFilename(override || projectName || 'MorphERD'),
    [projectName],
  )

  const resolveImageBaseName = useCallback((override?: string) => {
    const base = override || projectName || 'MorphERD'
    return sanitizeFilename(base)
  }, [projectName])

  const withCleanExportSelection = useCallback(async <T,>(task: () => Promise<T>) => {
    const selectionState = useSelectionStore.getState()
    const snapshot = {
      selectedTableId: selectionState.selectedTableId,
      selectedTableIds: new Set(selectionState.selectedTableIds),
      selectedRelationshipId: selectionState.selectedRelationshipId,
      selectedRelId: selectionState.selectedRelId,
      relMode: selectionState.relMode,
      relationshipSourceTableId: selectionState.relationshipSourceTableId,
      relSrcId: selectionState.relSrcId,
      relType: selectionState.relType,
    }

    useSelectionStore.setState({
      selectedTableId: null,
      selectedTableIds: new Set(),
      selectedRelationshipId: null,
      selectedRelId: null,
      relMode: false,
      relationshipSourceTableId: null,
      relSrcId: null,
    })

    try {
      return await task()
    } finally {
      useSelectionStore.setState(snapshot)
    }
  }, [])

  const exportImage = useCallback(async (format: 'png' | 'svg', overrideName?: string) => {
    const canvasElement = document.getElementById('cwrap')
    if (!canvasElement) {
      throw new Error('EXPORT_CANVAS_NOT_FOUND')
    }

    const hiddenTableIdSet = new Set(hiddenTableIds)
    const visibleTables = tables.filter((table) => !hiddenTableIdSet.has(table.id))
    if (!visibleTables.length) {
      return { ok: false as const, reason: 'empty-diagram' as const }
    }

    const visibleTableIds = new Set(visibleTables.map((table) => table.id))
    const visibleRelationships = rels.filter(
      (relationship) =>
        visibleTableIds.has(relationship.from) &&
        visibleTableIds.has(relationship.to),
    )

    const backgroundColor = getComputedStyle(canvasElement).backgroundColor || '#ffffff'
    const filename = `${resolveImageBaseName(overrideName)}.${format}`
    const exportParams = {
      element: canvasElement,
      tables: visibleTables,
      relationships: visibleRelationships,
      filename,
      backgroundColor,
      pixelRatio: 2,
      includeGrid: showGrid,
      theme: resolvedTheme,
    }

    await withCleanExportSelection(async () => {
      if (format === 'png') {
        await exportDiagramAsPng(exportParams)
        return
      }
      await exportDiagramAsSvg(exportParams)
    })

    return { ok: true as const }
  }, [hiddenTableIds, rels, resolveImageBaseName, resolvedTheme, showGrid, tables, withCleanExportSelection])

  const exportSql = useCallback(async (overrideName?: string) => {
    const target = resolveExportSource()
    if (!target) return { ok: false as const, reason: 'no-project' as const }
    const blob = await projectApi.exportSql(target.projectId, { source: target.source, commitId: target.commitId })
    triggerDownload(blob, `${resolveBaseName(overrideName)}.sql`)
    return { ok: true as const }
  }, [resolveBaseName])

  const exportDictionary = useCallback(async (overrideName?: string) => {
    const target = resolveExportSource()
    if (!target) return { ok: false as const, reason: 'no-project' as const }
    const blob = await projectApi.exportDictionary(target.projectId, {
      source: target.source,
      commitId: target.commitId,
      lang: language,
    })
    const t = i18n.getFixedT(language)
    triggerDownload(blob, `${resolveBaseName(overrideName)}-${t('export.dictionaryFileSuffix')}.md`)
    return { ok: true as const }
  }, [language, resolveBaseName])

  const exportVersionComparison = useCallback(async (input?: {
    overrideName?: string
    baseVersionId?: string
    targetVersionId?: string
  }) => {
    const target = resolveExportSource()
    if (!target) return { ok: false as const, reason: 'no-project' as const }
    const baseCommitId = Number(input?.baseVersionId)
    const targetCommitId = Number(input?.targetVersionId)
    if (!baseCommitId || !targetCommitId || baseCommitId === targetCommitId) {
      return { ok: false as const, reason: 'invalid-version-selection' as const }
    }
    const blob = await projectApi.versionReport(target.projectId, { baseCommitId, targetCommitId, lang: language })
    triggerDownload(blob, `${resolveBaseName(input?.overrideName)}-version-report.pdf`)
    return { ok: true as const }
  }, [language, resolveBaseName])

  const exportPng = useCallback((overrideName?: string) => exportImage('png', overrideName), [exportImage])

  const exportSvg = useCallback((overrideName?: string) => exportImage('svg', overrideName), [exportImage])

  return {
    exportSql,
    exportDictionary,
    exportVersionComparison,
    exportPng,
    exportSvg,
  }
}
